import type { GetMyClassEnrollmentsData } from '@dataconnect/generated';

import { useGetMyClassEnrollments } from '@dataconnect/generated/react';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

// ----------------------------------------------------------------------

/**
 * A single class the signed-in student is enrolled in (from `GetMyClassEnrollments`)
 * — the source of truth for the app-wide "active class". A student works inside one
 * class at a time; this context centralises that selection so every dashboard view
 * and the header switcher share it (and so it survives navigation + reload).
 *
 * Items are the *enrollment* objects, so consumers also get the per-student tutoring
 * settings (`curriculumScope`, `readingLevel`, …) for free. The class id lives at
 * `.class.id`.
 */
export type StudentClass = GetMyClassEnrollmentsData['classEnrollments'][number];

/**
 * The bell-schedule period this class meets in under the school's *default* daily
 * schedule, or null if none is assigned. A class can have a different period per
 * daily schedule; the default schedule answers "when does it normally meet".
 */
export function defaultSchedulePeriod(c: StudentClass) {
  return c.class.schedulePeriods?.find((sp) => sp.dailySchedule.isDefault)?.schedulePeriod ?? null;
}

const CLASS_KEY = 'student.activeClassId';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function persist(key: string, value: string | null) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable — selection just won't persist */
  }
}

type StudentClassContextValue = {
  /** All of the student's enrollments — what the class switcher shows. */
  classes: StudentClass[];
  activeClass: StudentClass | null;
  activeClassId: string | null;
  setActiveClassId: (id: string) => void;
  isLoading: boolean;
  /**
   * True while a live tutoring session is running in the Session view. The header
   * switcher disables itself when set so the student can't change class mid-session.
   */
  sessionLocked: boolean;
  setSessionLocked: (locked: boolean) => void;
};

const StudentClassContext = createContext<StudentClassContextValue | undefined>(undefined);

// ----------------------------------------------------------------------

export function StudentClassProvider({ children }: { children: React.ReactNode }) {
  const { data, isPending } = useGetMyClassEnrollments();
  const classes = useMemo(() => data?.classEnrollments ?? [], [data]);

  const [storedClassId, setStoredClassId] = useState<string | null>(() => read(CLASS_KEY));
  const [sessionLocked, setSessionLocked] = useState(false);

  // Resolve the active class: a stored id that still exists wins; otherwise the
  // first enrollment. (Rosters change year to year, so a persisted id may no
  // longer be valid.)
  const activeClass =
    (storedClassId && classes.find((c) => c.class.id === storedClassId)) || classes[0] || null;
  const activeClassId = activeClass?.class.id ?? null;

  // Keep state + storage converged on the resolved id once enrollments load
  // (handles first paint and stale ids).
  useEffect(() => {
    if (activeClassId && activeClassId !== storedClassId) {
      setStoredClassId(activeClassId);
      persist(CLASS_KEY, activeClassId);
    }
  }, [activeClassId, storedClassId]);

  const setActiveClassId = useCallback((id: string) => {
    setStoredClassId(id);
    persist(CLASS_KEY, id);
  }, []);

  const value = useMemo<StudentClassContextValue>(
    () => ({
      classes,
      activeClass,
      activeClassId,
      setActiveClassId,
      isLoading: isPending,
      sessionLocked,
      setSessionLocked,
    }),
    [classes, activeClass, activeClassId, setActiveClassId, isPending, sessionLocked]
  );

  return <StudentClassContext.Provider value={value}>{children}</StudentClassContext.Provider>;
}

// ----------------------------------------------------------------------

export function useActiveClass(): StudentClassContextValue {
  const ctx = useContext(StudentClassContext);
  if (!ctx) throw new Error('useActiveClass must be used within a StudentClassProvider');
  return ctx;
}
